import { Navigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../../services/authContext';
import { LoadingPage } from '../common/Spinner';
import { useProject } from '../../hooks/useProjects';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: string[];
}

export function ProtectedRoute({ children, requiredRoles }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <LoadingPage />;
  }

  if (!isAuthenticated) {
    // Redirect to login, preserving the intended destination
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check for required roles if specified
  if (requiredRoles && requiredRoles.length > 0) {
    const hasRequiredRole = requiredRoles.some((role) => user?.roles.includes(role));
    if (!hasRequiredRole) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  return <>{children}</>;
}

// Role-specific route wrappers
export function AdminRoute({ children }: { children: React.ReactNode }) {
  return <ProtectedRoute requiredRoles={['admin']}>{children}</ProtectedRoute>;
}

export function ManagerRoute({ children }: { children: React.ReactNode }) {
  return <ProtectedRoute requiredRoles={['admin', 'manager']}>{children}</ProtectedRoute>;
}

export function TaskManagerRoute({ children }: { children: React.ReactNode }) {
  return <ProtectedRoute requiredRoles={['admin', 'manager', 'task_manager']}>{children}</ProtectedRoute>;
}

export function SalesRoute({ children }: { children: React.ReactNode }) {
  return <ProtectedRoute requiredRoles={['admin', 'sales']}>{children}</ProtectedRoute>;
}

/**
 * MKS-347 — the Leads board, the one sales surface marketing may read.
 *
 * The sidebar has always shown Leads to marketing and the API has always let
 * marketing read them (`requireSalesOrMarketing` on every lead read), but the
 * route was `SalesRoute`, so the menu item led straight to `/unauthorized`.
 * This closes that gap on the side the backend already decided: marketing is
 * admitted, and `LeadsPage` renders read-only for them.
 */
export function LeadsBoardRoute({ children }: { children: React.ReactNode }) {
  return <ProtectedRoute requiredRoles={['admin', 'sales', 'marketing']}>{children}</ProtectedRoute>;
}

// Phase 27 D-04: 5 new role guards mirroring backend requireHR/Finance/Marketing/ISMS/ClientManager.
// Each includes 'admin' explicitly per D-12 OR/union (admin is superset).

export function HRRoute({ children }: { children: React.ReactNode }) {
  return <ProtectedRoute requiredRoles={['admin', 'hr']}>{children}</ProtectedRoute>;
}

export function FinanceRoute({ children }: { children: React.ReactNode }) {
  return <ProtectedRoute requiredRoles={['admin', 'finance']}>{children}</ProtectedRoute>;
}

/**
 * quick-260813-hob: `'admin'` is omitted ON PURPOSE — unlike every other
 * guard in the D-04 block above (whose comment says admin is always included
 * per D-12), this route is genuinely strict finance-only. `ProtectedRoute`
 * does a plain `roles.includes()` with no implicit admin, so
 * `requiredRoles={['finance']}` really does exclude an admin who lacks the
 * `finance` role. `FinanceRoute` above stays untouched — other pages rely on
 * its admin+finance semantics; do not repoint them at this guard.
 */
export function FinanceOnlyRoute({ children }: { children: React.ReactNode }) {
  return <ProtectedRoute requiredRoles={['finance']}>{children}</ProtectedRoute>;
}

export function MarketingRoute({ children }: { children: React.ReactNode }) {
  return <ProtectedRoute requiredRoles={['admin', 'marketing']}>{children}</ProtectedRoute>;
}

export function ISMSRoute({ children }: { children: React.ReactNode }) {
  return <ProtectedRoute requiredRoles={['admin', 'isms_manager']}>{children}</ProtectedRoute>;
}

export function ClientManagerRoute({ children }: { children: React.ReactNode }) {
  return <ProtectedRoute requiredRoles={['admin', 'client_manager']}>{children}</ProtectedRoute>;
}

// Phase 21.1: closes v5.0 milestone audit gap W-1 (PROJ-03/UI-05). Backend uses
// `requireAdminOrProjectPM`; this gate mirrors it so a non-admin PM can reach
// /projects/:id/edit and exercise the Task States section.
export function AdminOrProjectPMRoute({ children }: { children: React.ReactNode }) {
  return (
    <ProtectedRoute>
      <AdminOrProjectPMGate>{children}</AdminOrProjectPMGate>
    </ProtectedRoute>
  );
}

function AdminOrProjectPMGate({ children }: { children: React.ReactNode }) {
  const { user, isAdmin, isFinance } = useAuth();
  const { id } = useParams<{ id: string }>();
  const projectId = id ? parseInt(id, 10) : NaN;
  const validId = Number.isFinite(projectId) && projectId > 0;

  // MKS-94 — finance joins admin as a project-independent pass so a finance
  // user can reach the project's billing terms. ProjectFormPage renders ONLY
  // the billing-terms card for a finance user who is neither admin nor the
  // project's PM: the rest of the form stays manager+admin on the backend, so
  // showing it would offer saves that can only 403.
  const skipProjectFetch = isAdmin || isFinance;

  // Skip the project fetch for admins/finance (authorized regardless of PM) and
  // for invalid IDs (we'll redirect anyway). useProject's `enabled: !!id` short-circuits.
  const { data: project, isLoading } = useProject(
    !skipProjectFetch && validId ? projectId : undefined,
  );

  if (!validId) {
    return <Navigate to="/unauthorized" replace />;
  }

  if (skipProjectFetch) {
    return <>{children}</>;
  }

  if (isLoading) {
    return <LoadingPage />;
  }

  if (project?.projectManager?.id === user?.id) {
    return <>{children}</>;
  }

  return <Navigate to="/unauthorized" replace />;
}

/**
 * Manage-section gate (admin OR manager OR PM persona). Mirrors the
 * Sidebar v3 Manage section visibility (`effectiveManager OR
 * effectiveAdmin OR isPM`) so any user who sees a Manage link in the
 * sidebar can also reach the destination route without a 403 page.
 *
 * Backend scoping: routes guarded by this gate must rely on per-record
 * server-side scope (e.g. resolveAllowedProjectIds, F-17 / F-20) so a
 * PM landing on a list page sees only their own projects.
 */
export function AdminManagerOrPMRoute({ children }: { children: React.ReactNode }) {
  return (
    <ProtectedRoute>
      <AdminManagerOrPMGate>{children}</AdminManagerOrPMGate>
    </ProtectedRoute>
  );
}

function AdminManagerOrPMGate({ children }: { children: React.ReactNode }) {
  const { isAdmin, isManager, isPM } = useAuth();
  if (isAdmin || isManager || isPM) {
    return <>{children}</>;
  }
  return <Navigate to="/unauthorized" replace />;
}

/**
 * Report gate (admin OR finance OR manager OR PM persona). Mirrors the backend
 * `requireAdminFinanceManagerOrPM` guard, which since MKS-330 gates both
 * `GET /api/reports/project` and `GET /api/reports/project/detailed[/xlsx]`.
 *
 * MKS-330 retired the former `ProjectReportRoute` alias (= `AdminManagerOrPMRoute`,
 * F-17) that gated `/reports`, `/reports/project/detailed` and the reports
 * landing page: it omitted finance, so a finance-only user — who is granted
 * every other revenue report — was redirected to /unauthorized by a guard
 * stricter than the API behind it. Use this gate for report routes.
 *
 * Backend scopes rows per caller: admin and finance are unscoped, manager/PM
 * see only their PM-of projects (`resolveAllowedProjectIds`).
 */
export function AdminFinanceManagerOrPMRoute({ children }: { children: React.ReactNode }) {
  return (
    <ProtectedRoute>
      <AdminFinanceManagerOrPMGate>{children}</AdminFinanceManagerOrPMGate>
    </ProtectedRoute>
  );
}

function AdminFinanceManagerOrPMGate({ children }: { children: React.ReactNode }) {
  const { isAdmin, isManager, isPM, user } = useAuth();
  if (isAdmin || isManager || isPM || user?.roles.includes('finance')) {
    return <>{children}</>;
  }
  return <Navigate to="/unauthorized" replace />;
}

/**
 * Capacity-planning gate (admin OR PM persona only). Persona UAT 2026-05-14 —
 * Petra wants the global capacity matrix hidden from manager / HR / regular
 * users. Backend `requireCapacityActor` mirrors this same set.
 */
export function AdminOrPMRoute({ children }: { children: React.ReactNode }) {
  return (
    <ProtectedRoute>
      <AdminOrPMGate>{children}</AdminOrPMGate>
    </ProtectedRoute>
  );
}

function AdminOrPMGate({ children }: { children: React.ReactNode }) {
  const { isAdmin, isPM } = useAuth();
  if (isAdmin || isPM) {
    return <>{children}</>;
  }
  return <Navigate to="/unauthorized" replace />;
}

/**
 * Phase 106 / D-01 / D-09 — `'admin'` is omitted ON PURPOSE. Unlike every
 * other wrapper in this file, this guard deliberately BREAKS the D-12
 * "admin is a superset" convention: an admin who is not a superadmin must
 * NOT pass. Superadmin is a separate `User` column (D-09), not a `roles[]`
 * value, so it cannot be expressed via `requiredRoles` — hence a gate
 * component reading `useAuth().isSuperadmin` directly, following the
 * `AdminOrPMRoute` / `AdminOrPMGate` structural shape above rather than
 * `ProtectedRoute`'s `requiredRoles` mechanism.
 *
 * This is UX only. The real enforcement boundary is the backend's
 * `requireSuperadmin` middleware on `/api/fleet/*`, already proven by Plan
 * 106-07's integration suite — a determined user can always render any
 * component client-side, so this gate exists to keep the UI honest, not to
 * secure anything.
 */
export function SuperadminRoute({ children }: { children: React.ReactNode }) {
  return (
    <ProtectedRoute>
      <SuperadminGate>{children}</SuperadminGate>
    </ProtectedRoute>
  );
}

function SuperadminGate({ children }: { children: React.ReactNode }) {
  const { isSuperadmin } = useAuth();
  if (isSuperadmin) {
    return <>{children}</>;
  }
  return <Navigate to="/unauthorized" replace />;
}
